import { LuPlus } from "react-icons/lu";
import TextField from "../../../../common/TextField";
import {
    DEFAULT_UNGUIDED_TIER_LABEL,
    getCostTableSelectionState,
} from "../../../../../../utils/planManager/planConfigurationUtils.js";
import "./MemberCosts.scss";

/**
 * Editable cost-share table. Presentational — renders a checkable row per entry
 * with one text input per column, a master select-all header checkbox, and a New row control,
 * emitting all edits via callbacks. Owns no state.
 *
 * @param {object} props
 * @param {string} props.tableKey - Key identifying the table.
 * @param {string} [props.tableTitle] - Accessible title for table aria-labels.
 * @param {Array<Object>} props.rows - Rows to render (each has an `id` and `isChecked`).
 * @param {Array<{ field: string, label: string }>} props.columns - Column field/header pairs.
 * @param {Record<string, string>} [props.errors] - Field/row validation errors.
 * @param {(rowId: string, field: string, value: string) => void} props.onRowChange - Emits a cell edit.
 * @param {(rowId: string) => void} props.onRowToggle - Emits a row check toggle.
 * @param {(tableKey: string) => void} props.onToggleAll - Emits a master table check toggle.
 * @param {() => void} props.onAddRow - Emits a request to append an empty row.
 * @returns {import('react').JSX.Element}
 */
const CostTable = ({
    tableKey,
    tableTitle = "table",
    rows = [],
    columns,
    errors = {},
    onRowChange,
    onRowToggle,
    onToggleAll,
    onAddRow,
}) => {
    const { isAllChecked, isIndeterminate, hasRows } =
        getCostTableSelectionState(rows);

    return (
        <div className="plan-setup-member-costs__table-container">
            <div className="plan-setup-member-costs__table-wrapper">
                <table className="plan-setup-member-costs__table">
                    <thead>
                        <tr>
                            <th scope="col" className="plan-setup-member-costs__check-col">
                                <input
                                    type="checkbox"
                                    ref={(element) => {
                                        if (element) {
                                            element.indeterminate = isIndeterminate;
                                        }
                                    }}
                                    checked={isAllChecked}
                                    disabled={!hasRows}
                                    aria-label={`Select all ${tableTitle} rows`}
                                    onChange={() => onToggleAll?.(tableKey)}
                                />
                            </th>
                            {columns.map((column) => (
                                <th key={column.field} scope="col">
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => {
                            const rowCategoryError =
                                errors[`${tableKey}_${row.id}_category`];

                            return (
                                <tr key={row.id}>
                                    <td className="plan-setup-member-costs__check-col">
                                        <input
                                            type="checkbox"
                                            checked={row.isChecked}
                                            aria-label={`Include ${row.category || "row"}`}
                                            onChange={() => onRowToggle(row.id)}
                                        />
                                    </td>
                                    {columns.map((column) => (
                                        <td
                                            key={column.field}
                                            className={
                                                column.field === "category" &&
                                                rowCategoryError
                                                    ? "plan-setup-member-costs__cell--error"
                                                    : undefined
                                            }
                                        >
                                            <input
                                                type="text"
                                                value={row[column.field] ?? ""}
                                                aria-label={`${column.label} for ${
                                                    row.category || "row"
                                                }`}
                                                onChange={(event) =>
                                                    onRowChange(
                                                        row.id,
                                                        column.field,
                                                        event.target.value,
                                                    )
                                                }
                                            />
                                            {column.field === "category" &&
                                            rowCategoryError ? (
                                                <p
                                                    className="plan-setup-member-costs__cell-error"
                                                    role="alert"
                                                >
                                                    {rowCategoryError}
                                                </p>
                                            ) : null}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <button
                type="button"
                className="plan-setup-member-costs__add-row"
                onClick={onAddRow}
            >
                <LuPlus aria-hidden size={16} />
                New row
            </button>
        </div>
    );
};

/**
 * Member Costs step. Presentational — renders tier-label inputs and the medical,
 * Rx, and Rx specialty cost-share tables, emitting all edits upward. Owns no state.
 *
 * @param {object} props
 * @param {boolean} props.isHrp - Whether the plan is on the HRP platform tab.
 * @param {Object} props.values - The Member Costs slice of the wizard form state.
 * @param {string} props.unguidedTierLabelError - Validation message for the unguided tier label.
 * @param {Record<string, string>} [props.errors] - Field-level validation errors.
 * @param {(field: string, value: string) => void} props.onFieldChange - Emits a header/tier change.
 * @param {(checked: boolean) => void} props.onIncludeUnguidedColumnChange - Emits unguided column toggle.
 * @param {(tableKey: string, rowId: string, field: string, value: string) => void} props.onRowChange - Emits a row cell edit.
 * @param {(tableKey: string, rowId: string) => void} props.onRowToggle - Emits a row check toggle.
 * @param {(tableKey: string) => void} props.onToggleAll - Emits a master table check toggle.
 * @param {(tableKey: string) => void} props.onAddRow - Emits a request to append an empty row to a table.
 * @returns {import('react').JSX.Element}
 */
const MemberCosts = ({
    isHrp,
    values,
    unguidedTierLabelError = "",
    errors = {},
    onFieldChange,
    onIncludeUnguidedColumnChange,
    onRowChange,
    onRowToggle,
    onToggleAll,
    onAddRow,
}) => {
    const includeUnguidedColumn = Boolean(values.includeUnguidedColumn);
    const unguidedHeaderLabel =
        String(values.unguidedTierLabel ?? "").trim() ||
        DEFAULT_UNGUIDED_TIER_LABEL;

    const medicalColumns = [
        { field: "category", label: "Category" },
        { field: "inNetwork", label: values.tier1 || "Tier 1" },
        ...(includeUnguidedColumn
            ? [
                  {
                      field: "unguidedInNetwork",
                      label: unguidedHeaderLabel,
                  },
              ]
            : []),
        { field: "outOfNetwork", label: values.tier2 || "Tier 2" },
    ];
    const rxColumns = [
        { field: "category", label: "Category" },
        { field: "userInput1", label: values.rxHeader1 || "Custom Header 1" },
        { field: "userInput2", label: values.rxHeader2 || "Custom Header 2" },
    ];
    const rxSpecialtyColumns = [
        { field: "category", label: "Category" },
        {
            field: "userInput1",
            label: values.rxSpecialtyHeader1 || "Custom Header 1",
        },
        {
            field: "userInput2",
            label: values.rxSpecialtyHeader2 || "Custom Header 2",
        },
    ];

    return (
        <div className="plan-setup-step plan-setup-member-costs">
            <p className="plan-setup-member-costs__section-title">
                Member cost share inputs
            </p>

            {isHrp ? (
                <label className="plan-setup-member-costs__unguided-toggle">
                    <input
                        type="checkbox"
                        checked={includeUnguidedColumn}
                        aria-label="Include Unguided (In-Network) column"
                        onChange={(event) =>
                            onIncludeUnguidedColumnChange(event.target.checked)
                        }
                    />
                    Include Unguided (In-Network) column
                </label>
            ) : null}

            <div className="plan-setup-member-costs__headers">
                <TextField
                    id="tier-1"
                    label="Tier 1"
                    value={values.tier1}
                    error={errors.tier1}
                    onChange={(value) => onFieldChange("tier1", value)}
                />
                {includeUnguidedColumn ? (
                    <TextField
                        id="unguided-tier-label"
                        label="Unguided"
                        value={values.unguidedTierLabel}
                        error={unguidedTierLabelError}
                        onChange={(value) =>
                            onFieldChange("unguidedTierLabel", value)
                        }
                    />
                ) : null}
                <TextField
                    id="tier-2"
                    label="Tier 2"
                    value={values.tier2}
                    error={errors.tier2}
                    onChange={(value) => onFieldChange("tier2", value)}
                />
            </div>

            <CostTable
                tableKey="medicalRows"
                tableTitle="member cost share"
                rows={values.medicalRows}
                columns={medicalColumns}
                errors={errors}
                onRowChange={(rowId, field, value) =>
                    onRowChange("medicalRows", rowId, field, value)
                }
                onRowToggle={(rowId) => onRowToggle("medicalRows", rowId)}
                onToggleAll={onToggleAll}
                onAddRow={() => onAddRow("medicalRows")}
            />

            <p className="plan-setup-member-costs__section-title">Rx Coverage</p>
            <div className="plan-setup-member-costs__headers">
                <TextField
                    id="rx-header-1"
                    label="Custom Header 1"
                    value={values.rxHeader1}
                    onChange={(value) => onFieldChange("rxHeader1", value)}
                />
                <TextField
                    id="rx-header-2"
                    label="Custom Header 2"
                    value={values.rxHeader2}
                    onChange={(value) => onFieldChange("rxHeader2", value)}
                />
            </div>
            <CostTable
                tableKey="rxRows"
                tableTitle="Rx coverage"
                rows={values.rxRows}
                columns={rxColumns}
                errors={errors}
                onRowChange={(rowId, field, value) =>
                    onRowChange("rxRows", rowId, field, value)
                }
                onRowToggle={(rowId) => onRowToggle("rxRows", rowId)}
                onToggleAll={onToggleAll}
                onAddRow={() => onAddRow("rxRows")}
            />

            <p className="plan-setup-member-costs__section-title">Rx Specialty</p>
            <div className="plan-setup-member-costs__headers">
                <TextField
                    id="rx-specialty-header-1"
                    label="Rx Specialty Custom Header 1"
                    value={values.rxSpecialtyHeader1}
                    onChange={(value) =>
                        onFieldChange("rxSpecialtyHeader1", value)
                    }
                />
                <TextField
                    id="rx-specialty-header-2"
                    label="Rx Specialty Custom Header 2"
                    value={values.rxSpecialtyHeader2}
                    onChange={(value) =>
                        onFieldChange("rxSpecialtyHeader2", value)
                    }
                />
            </div>
            <CostTable
                tableKey="rxSpecialtyRows"
                tableTitle="Rx specialty"
                rows={values.rxSpecialtyRows}
                columns={rxSpecialtyColumns}
                errors={errors}
                onRowChange={(rowId, field, value) =>
                    onRowChange("rxSpecialtyRows", rowId, field, value)
                }
                onRowToggle={(rowId) => onRowToggle("rxSpecialtyRows", rowId)}
                onToggleAll={onToggleAll}
                onAddRow={() => onAddRow("rxSpecialtyRows")}
            />
        </div>
    );
};

export default MemberCosts;
